import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/utils/api'
import { useAuth } from './useAuth'

interface PermissionsResponse {
  role: string
  permissions: Record<string, boolean>
}

const ALL_GRANTED: Record<string, boolean> = {
  verify_chores: true,
  assign_chores: true,
  manage_meals: true,
  manage_events: true,
  manage_rewards: true,
  manage_users: true,
  manage_points: true,
  system_settings: true,
}

const NONE_GRANTED: Record<string, boolean> = {
  verify_chores: false,
  assign_chores: false,
  manage_meals: false,
  manage_events: false,
  manage_rewards: false,
  manage_users: false,
  manage_points: false,
  system_settings: false,
}

export function usePermissions() {
  const { user } = useAuth()

  const { data } = useQuery<PermissionsResponse>({
    queryKey: ['my-permissions'],
    queryFn: async () => {
      const response = await apiClient.get('/api/me/permissions')
      return response as unknown as PermissionsResponse
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000,
    retry: 1,
    enabled: !!user,
  })

  const role = data?.role ?? (user?.is_parent ? 'parent' : 'child')
  const permissions = data?.permissions ?? (user?.is_parent ? ALL_GRANTED : NONE_GRANTED)

  return {
    role,
    permissions,
    isParent: role === 'parent',
    isCaretaker: role === 'caretaker',
    isChild: role === 'child',
    hasPermission: (key: string) => {
      if (role === 'parent') return true
      if (role === 'child') return false
      return permissions[key] ?? false
    },
  }
}
